import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorSlideshowApplyPropertiesModal} from '@Modals/poster-editor-slideshow-apply-properties-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  removeImageSlideBackgroundFromId,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {onClickRemoveVideoBG, onOpenCropModal, onOpenMaskingModal, onOpenTrimVideoModal, onOpenVideoCropModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openPosterEditorSlideshowSlidesModal} from '@Modals/poster-editor-slideshow-slides-modal';
import {openPosterEditorItemFlipModal} from '@Modals/poster-editor-item-flip-modal';
import {openPosterEditorItemStretchModal} from '@Modals/poster-editor-item-stretch-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorAlignmentModal} from '@Modals/poster-editor-item-alignment-modal';
import {openPosterEditorItemFiltersModal} from '@Modals/poster-editor-item-filters-modal';
import {openPosterEditorItemAdjustmentsModal} from '@Modals/poster-editor-item-adjustments-modal';
import {openPosterEditorItemEffectsModal} from '@Modals/poster-editor-item-effects-modal';
import {openPosterEditorSlideshowTransitionModal} from '@Modals/poster-editor-slideshow-transition-modal';
import {openReplaceMediaModal} from '@Modals/replace-media-modal';
import {useEditorPrefetchMediaMobileModules} from '@Hooks/poster-editor/useEditorPrefetchMediaMobileModules';
import {MediaSlideBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {openPosterEditorRoundnessEffectModal} from '@Modals/poster-editor-roundness-effect-modal/poster-editor-roundness-effect-modal';
import styles from './media-slide-bottom-bar.module.scss';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyfromSlideshowItem} from '@Hooks/poster-editor/useGetKeyfromSlideshowItem';
import {useGetNumberOfSlidesForActiveSlideshowItem} from '@Hooks/poster-editor/useGetNumberOfSlidesForActiveSlideshowItem';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {SlideItemRemoveBgNotificationTag} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/slide-item-remove-bg-notification-tag/slide-item-remove-bg-notification-tag';
import {useAppSelector} from '@/hooks';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';
import {openPosterEditorItemVolumeModal} from '@Modals/poster-editor-item-volume-modal/poster-editor-item-volume-modal';
import {useGetTeamCredits} from '@Hooks/useGetTeamCredits';

export function MediaSlideBottomBar(): ReactElement | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const credits = useGetTeamCredits() ?? 0;
  String(credits);

  const isLocked = useGetKeyfromSlideshowItem('lockMovementX');
  useEditorPrefetchMediaMobileModules();
  const numOfImageSlides = useGetNumberOfSlidesForActiveSlideshowItem(ITEM_TYPE.IMAGESLIDE) ?? 0;
  const numOfVideoSlides = useGetNumberOfSlidesForActiveSlideshowItem(ITEM_TYPE.VIDEOSLIDE) ?? 0;
  const isBackgroundBeingRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''] as SlideshowItemObject | undefined;
    const slideItem = item?.slides.slidesHashMap[activeSlideUID ?? ''];
    if (slideItem && 'removeBackground' in slideItem) {
      return slideItem.removeBackground.isBackgroundBeingRemoved;
    }
    return false;
  });

  const isBackgroundRemoved = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId ?? ''] as SlideshowItemObject | undefined;
    const slideItem = item?.slides.slidesHashMap[activeSlideUID ?? ''];
    if (slideItem && 'removeBackground' in slideItem) {
      return slideItem.removeBackground.isBackgroundRemoved;
    }
    return false;
  });

  if (!activeItem?.isSlideshow()) {
    return null;
  }

  if (!activeSlide?.isMediaSlide()) {
    return null;
  }

  const pulsatingDotForCopyStyles = (): ReactElement | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (id: MediaSlideBottomBarOptions): void => {
    switch (id) {
      case MediaSlideBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case MediaSlideBottomBarOptions.CROP:
        if (activeSlide.isImageSlide()) {
          onOpenCropModal(activeSlide);
        } else {
          onOpenVideoCropModal(activeSlide, true);
        }
        break;
      case MediaSlideBottomBarOptions.REPLACE:
        openReplaceMediaModal();
        break;
      case MediaSlideBottomBarOptions.REMOVE_BG:
        void onRemoveBgClick();
        break;
      case MediaSlideBottomBarOptions.SLIDES:
        openPosterEditorSlideshowSlidesModal();
        break;
      case MediaSlideBottomBarOptions.STRETCH:
        openPosterEditorItemStretchModal();
        break;
      case MediaSlideBottomBarOptions.FLIP:
        openPosterEditorItemFlipModal();
        break;
      case MediaSlideBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case MediaSlideBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case MediaSlideBottomBarOptions.APPLY_TO_ALL:
        openPosterEditorSlideshowApplyPropertiesModal();
        break;
      case MediaSlideBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case MediaSlideBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      case MediaSlideBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case MediaSlideBottomBarOptions.MASK:
        if (activeSlide.isImageSlide()) {
          void onOpenMaskingModal(activeSlide);
        }
        break;
      case MediaSlideBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case MediaSlideBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;

      case MediaSlideBottomBarOptions.ALIGNMENT:
        openPosterEditorAlignmentModal();
        break;
      case MediaSlideBottomBarOptions.FILTERS:
        openPosterEditorItemFiltersModal();
        break;
      case MediaSlideBottomBarOptions.ADJUSTMENTS:
        openPosterEditorItemAdjustmentsModal();
        break;

      case MediaSlideBottomBarOptions.EFFECTS:
        openPosterEditorItemEffectsModal();
        break;

      case MediaSlideBottomBarOptions.TRANSITION:
        openPosterEditorSlideshowTransitionModal();
        break;
      case MediaSlideBottomBarOptions.VOLUME:
        openPosterEditorItemVolumeModal();
        break;
      case MediaSlideBottomBarOptions.UNMUTE:
        if (activeSlide.isVideoSlide()) {
          activeSlide.toggleMuteUnmuteState();
        }
        break;
      case MediaSlideBottomBarOptions.TRIM:
        if (activeSlide.isVideoSlide()) {
          onOpenTrimVideoModal(activeSlide);
        }
        break;
      case MediaSlideBottomBarOptions.ROUNDNESS:
        openPosterEditorRoundnessEffectModal();
        break;
      default:
        break;
    }
  };

  const onRemoveBgClick = async (): Promise<void> => {
    if (activeSlide.isImageSlide()) {
      if (isBackgroundRemoved && !isBackgroundBeingRemoved) {
        await activeSlide.disableRemoveBackgroundFlag();
        return;
      }
      removeImageSlideBackgroundFromId(activeItem.uid, activeSlide.uid);
    } else {
      await onClickRemoveVideoBG(activeSlide, credits);
    }
  };

  return (
    <BottomBar
      id="poster-editing-media-bottom-bar"
      key={activeSlide.uid}
      items={[
        {
          id: MediaSlideBottomBarOptions.SLIDES,
          icon: 'icon-slideshow',
          text: window.i18next.t('pmwjs_slides'),
        },
        {
          id: MediaSlideBottomBarOptions.CROP,
          icon: 'icon-crop',
          text: window.i18next.t('pmwjs_crop'),
        },
        {
          id: MediaSlideBottomBarOptions.TRIM,
          icon: 'icon-trim-video',
          text: window.i18next.t('pmwjs_trim'),
          isHidden: !activeSlide.isVideoSlide(),
        },
        {
          id: MediaSlideBottomBarOptions.UNMUTE,
          icon: activeSlide.isVideoSlide() && (!activeSlide.hasAudio || !activeSlide.isMuted()) ? 'icon-sound' : 'icon-sound-off',
          text: activeSlide.isVideoSlide() && activeSlide.isMuted() ? window.i18next.t('pmwjs_unmute') : window.i18next.t('pmwjs_mute'),
          isHidden: true,
          isDisabled: activeSlide.isVideoSlide() && !activeSlide.hasAudio,
        },
        {
          id: MediaSlideBottomBarOptions.VOLUME,
          icon: 'icon-sound',
          text: window.i18next.t('pmwjs_volume'),
          isDisabled: activeSlide.isVideoSlide() && !activeSlide.hasAudio,
        },
        {
          id: MediaSlideBottomBarOptions.TRANSITION,
          icon: 'icon-transition',
          text: window.i18next.t('pmwjs_transition'),
        },
        {
          id: MediaSlideBottomBarOptions.REMOVE_BG,
          icon: 'icon-remove-bg',
          text: window.i18next.t('pmwjs_ai_bg_remover'),
          tag: <SlideItemRemoveBgNotificationTag />,
          isTextBold: true,
          isLoading: isBackgroundBeingRemoved,
          isSelected: isBackgroundRemoved && !isBackgroundBeingRemoved,
          loadingText: window.i18next.t('pmwjs_removing_background'),
          textClasses: `spacing-p-0`,
          isDisabled: activeSlide.isImageSlide() && activeSlide.isImageUploading(),
        },
        {
          id: MediaSlideBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: MediaSlideBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: MediaSlideBottomBarOptions.MASK,
          icon: 'icon-mask',
          text: window.i18next.t('pmwjs_mask'),
          isHidden: !activeSlide.isImageSlide(),
        },
        {
          id: MediaSlideBottomBarOptions.ALIGNMENT,
          icon: 'icon-align-center',
          text: window.i18next.t('pmwjs_alignment'),
        },
        {
          id: MediaSlideBottomBarOptions.FILTERS,
          icon: 'icon-filters',
          text: window.i18next.t('pmwjs_filters'),
        },
        {
          id: MediaSlideBottomBarOptions.EFFECTS,
          icon: 'icon-effects',
          text: window.i18next.t('pmwjs_effects'),
        },
        {
          id: MediaSlideBottomBarOptions.ADJUSTMENTS,
          icon: 'icon-adjustments',
          text: window.i18next.t('pmwjs_adjustments'),
        },
        {
          id: MediaSlideBottomBarOptions.ROUNDNESS,
          icon: 'icon-corner-top-left',
          text: window.i18next.t('pmwjs_roundness'),
          isHidden: !activeSlide.isImageSlide(),
        },
        {
          id: MediaSlideBottomBarOptions.REPLACE,
          icon: 'icon-replace',
          text: window.i18next.t('pmwjs_replace'),
        },
        {
          id: MediaSlideBottomBarOptions.FLIP,
          icon: 'icon-flip-horizontal',
          text: window.i18next.t('pmwjs_flip'),
        },
        {
          id: MediaSlideBottomBarOptions.STRETCH,
          icon: 'icon-fit-to-width',
          text: window.i18next.t('pmwjs_stretch'),
        },
        {
          id: MediaSlideBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: MediaSlideBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: MediaSlideBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: MediaSlideBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: MediaSlideBottomBarOptions.APPLY_TO_ALL,
          icon: 'icon-apply-to-all-slides',
          text: window.i18next.t('pmwjs_apply_to_all_slides'),
          isHidden: (activeSlide.isImageSlide() && numOfImageSlides < 2) || (activeSlide.isVideoSlide() && numOfVideoSlides < 2),
        },
        {
          id: MediaSlideBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: MediaSlideBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
      onClick={onClickHandler}
    />
  );
}
